import { medusaClient } from '@lib/config'
import { Cart } from '@medusajs/medusa'
import Button from '@modules/common/components/button'
import Input from '@modules/common/components/input'
import Trash from '@modules/common/icons/trash'
import { formatAmount, useCart, useUpdateCart } from 'medusa-react'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { Text } from '@chakra-ui/react'

type DiscountFormValues = {
  discount_code: string
}

type DiscountCodeProps = {
  cart: Omit<Cart, 'refundable_amount' | 'refunded_total'>
}

const DiscountCode: React.FC<DiscountCodeProps> = ({ cart }) => {
  const { id, discounts, region } = cart
  const { mutate, isLoading } = useUpdateCart(id)
  const { setCart } = useCart()

  const { isLoading: mutationLoading, mutate: removeDiscount } = useMutation(
    (payload: { cartId: string; code: string }) => {
      return medusaClient.carts.deleteDiscount(payload.cartId, payload.code)
    }
  )

  const appliedDiscount = useMemo(() => {
    if (!discounts || !discounts.length) {
      return undefined
    }

    switch (discounts[0].rule.type as any) {
      case 'percentage':
        return `${discounts[0].rule.value}%`
      case 'fixed':
        return `- ${formatAmount({
          amount: discounts[0].rule.value,
          region: region
        })}`
      case 'fixed_price':
        return `Fixed ${formatAmount({
          amount: discounts[0].rule.value,
          region: region
        })}`

      case 'min_spend': {
        const minSpend = formatAmount({
          amount: (discounts[0].metadata.total || 0) as number,
          region: region
        })

        const value = formatAmount({
          amount: discounts[0].rule.value,
          region: region
        })

        return `Get ${value} off when you spend ${minSpend}`
      }

      default:
        return 'Free shipping'
    }
  }, [discounts, region])

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<DiscountFormValues>({
    mode: 'onSubmit'
  })

  const onApply = (data: DiscountFormValues) => {
    mutate(
      {
        discounts: [{ code: data.discount_code }]
      },
      {
        onSuccess: ({ cart }) => {
          setCart(cart)
        },
        onError: () => {
          setError(
            'discount_code',
            {
              message: 'Voucher is not valid'
            },
            {
              shouldFocus: true
            }
          )
        }
      }
    )
  }

  const onRemove = () => {
    removeDiscount(
      { cartId: id, code: discounts[0].code },
      {
        onSuccess: ({ cart }) => {
          setCart(cart)
        }
      }
    )
  }

  return (
    <div className="flex flex-col w-full bg-white">
      <div className="mb-4">
        <h3 className="text-base-semi">Discount</h3>
      </div>
      <div className="text-small-regular">
        {appliedDiscount ? (
          <div className="flex items-center justify-between">
            <div>
              <span>Code: </span>
              <span className="font-semibold">{appliedDiscount}</span>
            </div>
            <div>
              <button
                className="flex items-center gap-x-2"
                onClick={onRemove}
                disabled={isLoading}
              >
                <Trash size={16} />
                <span className="sr-only">Remove gift card from order</span>
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onApply)} className="w-full">
            <div className="grid grid-cols-[1fr_80px] gap-x-2">
              <Input
                label="Code"
                {...register('discount_code', {
                  required: 'Code is required'
                })}
              />
              <div>
                <Button
                  className="!min-h-[0] h-[60px] w-[80px]"
                  disabled={isLoading}
                  isLoading={isLoading}
                >
                  Apply
                </Button>
              </div>
            </div>
            <>
              {errors && errors.discount_code && (
                <Text fontSize={'sm'} textColor={'#FF0000'} mt={1}>
                  {errors.discount_code?.message}
                </Text>
              )}
            </>
          </form>
        )}
      </div>
    </div>
  )
}

export default DiscountCode
